/*************************************************************************
 * ChooseYourAdventure.tsx  (enhanced storytelling version)
 *************************************************************************/
import React, { useContext } from "react";
import {
  View,
  StyleSheet,
  Image,
  Platform,
  Pressable,
  useWindowDimensions,
  Linking,
} from "react-native";
import Video from "react-native-video";
import { useNavigate } from "react-router-dom";

import { NormalText, PrimaryButton } from "../../../common/native";
import { mixpanel } from "../../../../utils/mixpanel";
import { useOnboardingSettings } from "../../../../contexts/OnboardingSettingsContext";
import { SessionContext } from "../../../../contexts";

type LinkChip = { label: string; url: string };

type AdventureCard = {
  id: "lead" | "beforeAfter" | "consult";
  title: string;
  story: string;
  how: string[];
  mediaUri: string;
  isVideo: boolean;
  cta: string;
  route: string;
  links: LinkChip[];
};

const CARDS: AdventureCard[] = [
  {
    id: "lead",
    title: "Engage New Leads From Your Website",
    story:
      "A visitor is never more excited than the moment they’re on your site.\n" +
      "Let them play with realistic AI simulations and happily hand over their contact info—before a competitor steals their attention.",
    how: [
      "Embed the widget in 5 min (we’ll do it free if you’re busy)",
      "Simulations spark curiosity → higher form‑fills",
      "Leads & images flow straight into your CRM",
    ],
    mediaUri:
      "https://pub-0991182fdab143cf938953a8d6ab8813.r2.dev/video-assets/simulate-procedure-chatbot-trimmed.mp4",
    isVideo: true,
    cta: "Simulate Now",
    route: "/simulate",
    links: [
      { label: "Widget Instructions", url: "/deploy-widget" },
      //   { label: "Try Simulate Tool", url: "/simulate" },
      { label: "View Leads", url: "/patients?filter=leads" },
    ],
  },
  {
    id: "beforeAfter",
    title: "Create Stunning Before / Afters",
    story:
      "Dr Palladino closes the loop with every patient by sending a beautiful, branded Before/After image during recovery—boosting 5‑star reviews and word‑of‑mouth.\n" +
      "Every patient deserves that moment of pride.",
    how: [
      "Generate perfectly aligned, branded images in seconds",
      "Or connect your EHR for fully‑automated delivery",
    ],
    mediaUri:
      "https://pub-0991182fdab143cf938953a8d6ab8813.r2.dev/video-assets/before-after-recording-trimmed.mp4",
    isVideo: true,
    cta: "Generate Before‑Afters",
    route: "/before-afters",
    links: [
      //   { label: "Before‑After Tool", url: "/before-afters" },
      { label: "Before‑After Gallery", url: "/gallery" },
    ],
  },
  {
    id: "consult",
    title: "Arrive Prepared to Every Consult",
    story:
      "Great consults start with sharp preparation—hard to achieve when your team is stretched thin.\n" +
      "ClinicOS puts every key detail in one page, ready before you walk to the appointment.",
    how: [
      "High‑level patient snapshot in seconds",
      "Record, transcribe & summarize every interaction",
      "Instant access to patient photos + simulations",
      "See past cases with similar body types",
    ],
    mediaUri: "https://clinic-os.com/video-assets/consults-recording.mp4", // Replace with real demo
    isVideo: true,
    cta: "Start Prep",
    route: "/consults",
    links: [{ label: "Patient Timeline", url: "/consults" }],
  },
];

const ChooseYourAdventure: React.FC = () => {
  const { width, height } = useWindowDimensions();
  const isMobile = Math.min(width, height) < 644;

  const session = useContext(SessionContext)?.session;
  const navigate = useNavigate();
  const {
    setTourActive,
    setShowSimulateTips,
    setShowBeforeAfterTips,
    setShowConsultTips,
  } = useOnboardingSettings();

  const handleCTA = (card: AdventureCard) => {
    mixpanel.track("Homepage Link Clicked", {
      workflow: card.id,
      link: card.route,
      "Clinic Name": session?.clinicName ?? "clinic not found",
    });
    // setTourActive(true);
    // if (card.id === "lead") setShowSimulateTips(true);
    // if (card.id === "beforeAfter") setShowBeforeAfterTips(true);
    // if (card.id === "consult") setShowConsultTips(true);
    navigate(card.route);
  };

  return (
    <View
      style={[
        styles.wrapper,
        { flexDirection: isMobile ? "column" : "row", gap: isMobile ? 32 : 24 },
      ]}
    >
      {CARDS.map((card) => (
        <View
          key={card.id}
          style={[
            styles.card,
            { width: isMobile ? "100%" : "33%", maxWidth: 500 },
          ]}
        >
          {/* media */}
          {card.isVideo ? (
            Platform.OS === "web" ? (
              <video
                src={card.mediaUri}
                style={styles.media}
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <Video
                source={{ uri: card.mediaUri }}
                style={styles.media}
                repeat
                muted
                resizeMode="cover"
              />
            )
          ) : (
            <Image source={{ uri: card.mediaUri }} style={styles.media} />
          )}

          {/* title */}
          <NormalText
            text={card.title}
            fontWeight="700"
            style={styles.title}
            numberOfLines={3}
          />

          {/* story */}
          <NormalText
            text={card.story}
            fontWeight="400"
            style={styles.story}
            numberOfLines={6}
          />

          {/* how list */}
          {/* <View style={styles.list}>
            {card.how.map((line) => (
              <NormalText
                key={line}
                text={`•  ${line}`}
                fontWeight="400"
                style={styles.listItem}
              />
            ))}
          </View> */}

          {/* CTA */}
          <PrimaryButton
            text={card.cta}
            width={220}
            roundBorderRadius
            onPress={() => handleCTA(card)}
          />

          {/* links */}
          <View style={styles.linkRow}>
            {card.links.map((l) => (
              <Pressable
                key={l.label}
                style={styles.linkChip}
                onPress={() => {
                  mixpanel.track("Homepage Link Clicked", {
                    workflow: card.id,
                    link: l.label,
                  });
                  if (Platform.OS === "web" && l.url.startsWith("/")) {
                    navigate(l.url);
                  } else {
                    Linking.openURL(l.url);
                  }
                }}
              >
                <NormalText text={l.label} style={styles.linkText} />
              </Pressable>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  card: {
    backgroundColor: "#f9f9f9",
    borderRadius: 16,
    padding: 20,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
    alignItems: "center",
    minHeight: 500,
    minWidth: 300,
  },
  media: {
    width: "100%",
    aspectRatio: 16 / 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    color: "#111",
    marginBottom: 6,
    textAlign: "center",
  },
  story: {
    fontSize: 15,
    color: "#444",
    marginBottom: 12,
    textAlign: "center",
    lineHeight: 21,
  },
  list: {
    width: "100%",
    gap: 4,
    marginBottom: 16,
  },
  listItem: {
    fontSize: 14,
    color: "#444",
  },
  linkRow: {
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 16,
    justifyContent: "center",
  },
  linkChip: {
    backgroundColor: "#EAF6FF",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  linkText: {
    fontSize: 13,
    color: "#00B7F4",
    textAlign: "center",
  },
});

export default ChooseYourAdventure;
